import { ForexStatus } from "@/feature/Forex/api/history/forex-history.interface";
import { ForexCurrency } from "@/feature/Forex/store/interface";
import { ForexOperationType } from "@/feature/Forex/api/forex.interface";
import { FormItemCommon } from "@/components/Form/interface";
import { FileObjectItem } from "@/api/files/interface";

export enum ForexAccountVerifyingStatus {
	SUCCESS = 'SUCCESS',
	FUNDS_LACK = 'FUNDS_LACK',
	WRONG_DATE = 'WRONG_DATE',
	RESERVED_FUNDS_LACK = 'RESERVED_FUNDS_LACK',
	CONTRACT_NOT_ACTIVE = 'CONTRACT_NOT_ACTIVE',
	RATE_CHANGED = 'RATE_CHANGED',
}

export enum ForexBuyField {
	requestAmount = 'requestAmount',
	purpose = 'purpose',
	course = 'course',
	currency = 'currency',
	amountUzs = 'amountUzs',
	account = 'account',
	reservingAccount = 'reservingAccount',
	loanAccount = 'loanAccount',
	receiverAccount = 'receiverAccount',
	phoneNumber = 'phoneNumber',
	contractNumber = 'contractNumber',
	contractDate = 'contractDate',
	contractId = 'contractId',
	contractEndDate = 'contractEndDate',
	partnerName = 'partnerName',
	partnerCountry = 'partnerCountry',
	partnerCountryCode = 'partnerCountryCode',
	serviceDescription = 'serviceDescription',
	otherServiceDetails = 'otherServiceDetails',
	otherPurposeDetails = 'otherPurposeDetails',
	fundsSource = 'fundsSource',
	currencyAccount = 'currencyAccount',
	mfo = 'mfo',
	fileAttachments = 'fileAttachments',
	foreignExchangeId = 'foreignExchangeId',
	operDay = 'operDay',
	operationType = 'operationType',
	templateName = 'templateName',
	contractAmount = 'contractAmount',
}

export enum ForexBuyActionKey {
	SAVE_DRAFT = 'SAVE_DRAFT',
}

export enum ForexBuyPurpose {
	CODE_001 = 'CODE_001',
	CODE_002 = 'CODE_002',
	CODE_003 = 'CODE_003',
	CODE_004 = 'CODE_004',
	CODE_005 = 'CODE_005',
	CODE_006 = 'CODE_006',
	CODE_007 = 'CODE_007',
	CODE_008 = 'CODE_008',
	CODE_009 = 'CODE_009',
	CODE_010 = 'CODE_010',
	CODE_011 = 'CODE_011',
	CODE_012 = 'CODE_012',
	CODE_013 = 'CODE_013',
	CODE_014 = 'CODE_014',
}

export enum ForexBuyType {
	TRANSPORTATION = 'TRANSPORTATION',
	CONSULTING = 'CONSULTING',
	ADVERTISING = 'ADVERTISING',
	REPAIR = 'REPAIR',
	COMMUNICATION = 'COMMUNICATION',
	INSURANCE = 'INSURANCE',
	FINANCIAL = 'FINANCIAL',
	ROYALTIES = 'ROYALTIES',
	INFORMATION = 'INFORMATION',
	CONSTRUCTION = 'CONSTRUCTION',
	OTHER = 'OTHER',
}

export enum ForexBuyTarget {
	IMPORT = 'IMPORT',
}

export enum ForexFoundsSource {
	EXPORT_EARNINGS = 'EXPORT_EARNINGS',
	LOANS_RECEIVED_FROM_DOMESTIC_BANK = 'LOANS_RECEIVED_FROM_DOMESTIC_BANK',
	LOANS_FROM_ABROAD = 'LOANS_FROM_ABROAD',
	REVERSE_SALE_UNUSED_CURRENCY = 'REVERSE_SALE_UNUSED_CURRENCY',
	OTHER_REFERENCES = 'OTHER_REFERENCES',
}

export interface ForexBuyDeletedFile {
	message: string
}

export interface ForexBuyContract {
	amount2: number | null
	bankForCountryCode: string | null
	bankForName: string | null
	bankForRequisites: string | null
	branch: string | null
	contractAmount: number | null
	contractDate: string | null
	contractEndDate: string | null
	contractForName: string | null
	contractNumber: string | null
	contractStatus: string | null
	contractStatusName: string | null
	contractType: string | null
	contractTypeCode: string | null
	contractTypeName: string | null
	contractorForCountryName: string | null
	contractUzName: string | null
	contractorForCountryCode: string | null
	contractorUzParentCompany: string | null
	currCode1: string | null
	idn: string | null
	okonx: string | null
	partnerCountry: string | null
	contractorForName: string | null
	productsName: string | null
}

export interface ForexBuyDraft {
	operationType?: ForexOperationType | null
	operDay?: string | null // YYYY-MM-DD
	createdAt?: string | null // YYYY-MM-DD
	currency?: ForexCurrency | null
	requestAmount?: number | null
	purpose?: ForexBuyPurpose | null | ''
	course?: number | null
	amountUzs?: number | null
	account?: string | null
	reservingAccount?: string | null
	receiverAccount?: string | null
	phoneNumber?: string | null
	loanAccount?: string | null
	contractNumber?: string | null
	contractDate?: string | null // YYYY-MM-DD
	contractId?: string | null
	contractEndDate?: string | null // YYYY-MM-DD
	partnerName?: string | null
	partnerCountry?: string | null
	partnerCountryCode?: string | null
	contractAmount?: number | null
	serviceDescription?: ForexBuyType | null | ''
	otherServiceDetails?: string | null
	otherPurposeDetails?: string | null
	isFundsTransferAllowed?: boolean | null
	fundsSource?: ForexFoundsSource | null | ''
	mfo?: string | null
	currencyAccount?: string | null
	fundsTransferAllowed?: boolean | null
	foreignExchangeId?: number | null
	fileAttachments?: ForexBuyUploadedFile[] | null
	status?: ForexStatus | null
	fundSourceText?: string | null
	hidden?: boolean | undefined //for hiding cards after delete
}

export interface ForexBuyUploadedFile extends FileObjectItem {
	foreignExchangeId?: number | null
}

export interface ForexBuyIdnSearchParams {
	idn?: string
}

export interface ForexAccountVerifying {
	status?: ForexAccountVerifyingStatus | null
	foreignExchangeId?: number | null
	actualCourse?: number | null
	previousCourse?: number | null
	newAmountUzs?: number | null
	previousAmountUzs?: number | null
	missingAmount?: number | null
}

export interface ForexCountrySearchParams {
	nameOnEnglish?: string
	name?: string
	code?: string
	generalSearchParameter?: string
}

export interface ForexCountrySearchResult {
	id?: number
	code?: string
	name?: string
	nameOnEnglish?: string
	fullName?: string
	alpha2?: string
	alpha3?: string
	partOfTheWorld?: string
	placement?: string
	nameToShow?: string
}

export type ForexAccountVerifyingHandlers = {
	[P in ForexAccountVerifyingStatus]?: (
		draft: ForexBuyDraft,
		resultOfChecking: ForexAccountVerifying
	) => any
}

export type ForexAccountVerifyingConfirmationHandlers = {
	[P in ForexAccountVerifyingStatus]?: (
		confirmed: boolean,
		draft?: ForexBuyDraft,
		resultOfAccountVerifying?: ForexAccountVerifying,
	) => any
}

export type ForexAccountVerifyingFieldHandlers = {
	[P in ForexBuyField]?: (
		field: FormItemCommon<ForexBuyField>
	) => any
}